import UntitledIcon from '@payaca/untitled-icons';
import React, { useMemo } from 'react';
import Button from '../Button/Button';
import { useToastContext } from '../Toast/ToastContext';
import TreeView, { TreeViewItem } from './TreeView';

interface JSONSchema {
  type?: string | string[];
  properties?: Record<string, JSONSchema>;
  required?: string[];
  const?: string; // for constant values
  anyOf?: JSONSchema[];
  [key: string]: any; // for other metadata
}

const findSchemaType = (schema: JSONSchema): string | string[] | undefined => {
  if ('const' in schema) {
    return `'${schema.const}'`;
  } else if ('type' in schema) {
    return schema.type;
  } else if ('anyOf' in schema) {
    return schema.anyOf
      ?.flatMap((item) => findSchemaType(item))
      .filter(Boolean) as string[];
  }
  return undefined;
};

const findObjectProperties = (
  schema: JSONSchema
): Record<string, JSONSchema> | undefined => {
  // Direct properties on the schema
  if (schema.properties) {
    return schema.properties;
  }

  // For nullable objects (anyOf with object and null), find the object schema
  if (schema.anyOf) {
    for (const item of schema.anyOf) {
      const itemType = findSchemaType(item);
      const isObjectType = Array.isArray(itemType)
        ? itemType.includes('object')
        : itemType === 'object';

      if (isObjectType && item.properties) {
        return item.properties;
      }
    }
  }

  return undefined;
};

const convertJSONSchemaToTreeItems = (
  schema: JSONSchema,
  recursiveKeys: string[] = []
): TreeViewItem[] => {
  const properties = findObjectProperties(schema);

  if (!properties) {
    return [];
  }

  return Object.entries(properties).map(([key, childSchema]) => {
    const schemaType = findSchemaType(childSchema);
    const isObjectType = Array.isArray(schemaType)
      ? schemaType.includes('object')
      : schemaType === 'object';

    const currentKeys = [...recursiveKeys, key];

    const item: TreeViewItem = {
      id: currentKeys.join('.'),
      label: key,
      metaData: {
        type: schemaType,
        recursiveKeys: currentKeys,
      },
    };

    if (isObjectType) {
      const childProperties = findObjectProperties(childSchema);
      if (childProperties) {
        item.children = convertJSONSchemaToTreeItems(childSchema, currentKeys);
      }
    }

    return item;
  });
};

interface SchemaTreeViewProps {
  schema: JSONSchema;
  canCopyToClipboard?: boolean;
}

const SchemaTreeView: React.FC<SchemaTreeViewProps> = (props) => {
  const { schema, canCopyToClipboard = false } = props;

  const { pushSuccess } = useToastContext();

  const handleCopy = async (text: string) => {
    await navigator.clipboard.writeText(text);
    pushSuccess('Copied to clipboard');
  };

  const collectSchemaRows = (
    schemaNode: JSONSchema,
    parentKeys: string[] = []
  ): { variable: string; type: string }[] => {
    const properties = findObjectProperties(schemaNode);
    if (!properties) {
      return [];
    }

    const rows: { variable: string; type: string }[] = [];

    for (const [key, childSchema] of Object.entries(properties)) {
      const currentKeys = [...parentKeys, key];
      const schemaType = findSchemaType(childSchema);
      const typeStr = Array.isArray(schemaType)
        ? schemaType.join(' \\| ')
        : schemaType || 'unknown';

      const variablePath = currentKeys.reduce((acc, k) => {
        if (/^[A-Za-z]+$/.test(k)) {
          return `${acc}${acc ? '.' : ''}${k}`;
        } else {
          return `${acc}['${k}']`;
        }
      }, '');

      rows.push({
        variable: `\`{{ ${variablePath} }}\``,
        type: typeStr,
      });

      const childProperties = findObjectProperties(childSchema);
      if (childProperties) {
        const childRows = collectSchemaRows(childSchema, currentKeys);
        rows.push(...childRows);
      }
    }

    return rows;
  };

  const convertSchemaToMarkdown = (schemaNode: JSONSchema): string => {
    const rows = collectSchemaRows(schemaNode, []);

    if (rows.length === 0) {
      return '';
    }

    const header = '| Variable | Type |\n|----------|------|';
    const tableRows = rows.map((row) => `| ${row.variable} | ${row.type} |`);

    return [header, ...tableRows].join('\n');
  };

  const handleCopyMarkdown = async () => {
    const schemaMarkdown = convertSchemaToMarkdown(schema);
    await navigator.clipboard.writeText(schemaMarkdown);
    pushSuccess(
      'Copied all variables to clipboard, paste into your AI chat tool!'
    );
  };

  const labelRenderer = (item: TreeViewItem) => {
    const { type, recursiveKeys } = item.metaData || {};
    const types = Array.isArray(type) ? type : [type];

    const variableCopyText = `{{ ${
      recursiveKeys?.reduce((acc: string, recursiveKey: string) => {
        if (/^[A-Za-z]+$/.test(recursiveKey)) {
          return `${acc}${acc ? '.' : ''}${recursiveKey}`;
        } else {
          return `${acc}['${recursiveKey}']`;
        }
      }, '') || ''
    } }}`;

    return (
      <div
        className="grow hover:bg-gray-100 rounded-md cursor-pointer px-2"
        onClick={() => handleCopy(variableCopyText)}
      >
        <div className="flex gap-2 items-baseline">
          <span className="font-semibold">{item.label}</span>
          <span className="supporting-body">{types.join(' or ')}</span>
        </div>
        <span className="supporting-body text-gray-500">
          {variableCopyText}
        </span>
      </div>
    );
  };

  const items = useMemo(() => convertJSONSchemaToTreeItems(schema), [schema]);

  return (
    <div>
      {canCopyToClipboard && (
        <Button
          className="mb-2"
          variant="link-inline"
          linkColour="dark"
          onClick={handleCopyMarkdown}
        >
          <UntitledIcon className="size-4" name="copy-07.3" />
          Copy all variables for AI Agents
        </Button>
      )}

      <TreeView items={items} labelRenderer={labelRenderer} />
    </div>
  );
};

export default SchemaTreeView;
